import { IconButton } from '@orbitalwatch/design';
import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from './icons';

export interface FullScreenLayerProps {
    /** Display title in the head; names the dialog. */
    title: ReactNode;
    /** 11 px uppercase label above the title ("Page history", "Editing"). */
    eyebrow?: ReactNode;
    /** Esc and the Close button call it (the host unmounts the layer or sets `open={false}`). */
    onClose: () => void;
    /** Default `true`. */
    open?: boolean;
    /** Buttons on the right of the head, before Close (Save, Cancel). */
    actions?: ReactNode;
    /** Accessible name of the Close button. Default "Close". */
    closeLabel?: string;
    /** No body padding (an editor that fills the layer). */
    flush?: boolean;
    className?: string;
    children: ReactNode;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * DS gap: a full-viewport modal layer for the editor, page history and media viewer (the old 95vw × 90vh popups). The
 * library `Dialog` stops at 640 px; this is the same surface-3 sheet over the scrim, at full size: a head with title,
 * actions and Close, a scrolling body; focus moves in, is trapped, Esc closes, focus returns to the trigger
 * (`ds.css` "full-screen layer").
 */
export function FullScreenLayer(props: FullScreenLayerProps) {
    const open = props.open ?? true;
    const headingId = useId();
    const layerRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(props.onClose);
    useEffect(() => {
        onCloseRef.current = props.onClose;
    }, [props.onClose]);

    useEffect(() => {
        if (!open) return;
        const previous = document.activeElement as HTMLElement | null;
        const layer = layerRef.current;
        // the first control of the body (the editor), else the head's buttons
        const first = layer?.querySelector('.ark-layer-body')?.querySelector<HTMLElement>(FOCUSABLE)
            ?? layer?.querySelector<HTMLElement>(FOCUSABLE);
        first?.focus();
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                // a menu or dialog opened inside the layer handles its own Esc
                if (event.defaultPrevented) return;
                event.stopPropagation();
                onCloseRef.current();
                return;
            }
            if (event.key !== 'Tab' || !layer) return;
            const items = Array.from(layer.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => el.offsetParent !== null || el === document.activeElement);
            if (items.length === 0) return;
            const firstItem = items[0];
            const lastItem = items[items.length - 1];
            if (event.shiftKey && document.activeElement === firstItem) {
                event.preventDefault();
                lastItem.focus();
            } else if (!event.shiftKey && document.activeElement === lastItem) {
                event.preventDefault();
                firstItem.focus();
            }
        };
        layer?.addEventListener('keydown', onKey);
        return () => {
            layer?.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflow;
            previous?.focus?.();
        };
    }, [open]);

    if (!open) return null;
    return createPortal(
        <div className="ark-layer-scrim">
            <div
                ref={layerRef}
                className={['ark-layer', props.className].filter(Boolean).join(' ')}
                role="dialog"
                aria-modal="true"
                aria-labelledby={headingId}
            >
                <div className="ark-layer-head">
                    <div className="ark-layer-title">
                        {props.eyebrow ? <span className="ark-layer-eyebrow">{props.eyebrow}</span> : null}
                        <h2 id={headingId}>{props.title}</h2>
                    </div>
                    <div className="ark-layer-actions">
                        {props.actions}
                        <IconButton icon={<Icons.close size={18} />} aria-label={props.closeLabel ?? 'Close'} onClick={() => props.onClose()} />
                    </div>
                </div>
                <div className={props.flush ? 'ark-layer-body is-flush' : 'ark-layer-body'}>{props.children}</div>
            </div>
        </div>,
        document.body,
    );
}
